import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

interface OosStamp {
  at?: string;
  tractor?: string;
  was_confirmed?: boolean;
}

function readStamp(row: unknown): OosStamp | null {
  const meta = (row as { metadata?: unknown } | null)?.metadata;
  if (!meta || typeof meta !== "object") return null;
  const stamp = (meta as Record<string, unknown>).oos_unassign;
  if (!stamp || typeof stamp !== "object") return null;
  return stamp as OosStamp;
}

/**
 * Realtime listener for tractor out-of-service unassignments.
 *
 * The `/truck-status` edge function reacts to blocking OOS events by calling
 * `handle_tractor_oos_unassign`, which strips the tractor, drops confirmed
 * orders back to `booked`, and stamps `orders.metadata.oos_unassign` with a
 * fresh timestamp. `public.orders` has REPLICA IDENTITY FULL, so the realtime
 * payload carries both `old` and `new` — we only react when the stamp changes,
 * which distinguishes an OOS eviction from an ordinary manual unassign.
 *
 * @param orderId when provided, only that order raises a toast (Order Detail).
 */
export function useOrderOosRealtime(orderId?: string) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!tenantId) return;

    const channel = supabase
      .channel(`orders-oos-${tenantId}${orderId ? `-${orderId}` : ""}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "orders",
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const next = payload.new as { id?: string; unique_id?: string } | null;
          const stamp = readStamp(payload.new);
          const prevStamp = readStamp(payload.old);
          if (!stamp?.at || stamp.at === prevStamp?.at) return;
          if (orderId && next?.id !== orderId) return;

          const dedupeKey = `${next?.id ?? "?"}:${stamp.at}`;
          if (seenRef.current.has(dedupeKey)) return;
          seenRef.current.add(dedupeKey);

          // Cache bust: list, detail, children and the OOS status map that
          // feeds the truck pills / scheduler bands.
          queryClient.invalidateQueries({ queryKey: ["orders", tenantId] });
          queryClient.invalidateQueries({ queryKey: ["order-detail"] });
          queryClient.invalidateQueries({ queryKey: ["order-children"] });
          // Per-leg asset caches live under their own keys and are NOT
          // prefix-matched by ["order-children"].
          queryClient.invalidateQueries({ queryKey: ["order-children-legs"] });

          queryClient.invalidateQueries({ queryKey: ["tractor-oos-status-map"] });

          const label = next?.unique_id ?? "An order";
          const truck = stamp.tractor ?? "A truck";
          toast.warning(`${truck} is out of service`, {
            description: `${label} was unassigned${
              stamp.was_confirmed ? " and moved back to Booked" : ""
            }.`,
            duration: 10_000,
            ...(next?.id && !orderId
              ? {
                  action: {
                    label: "View order",
                    onClick: () => navigate(`/orders/${next.id}`),
                  },
                }
              : {}),
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [tenantId, orderId, queryClient, navigate]);
}